import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const services = [
  {
    id: 1,
    icon: 'ri-award-line',
    title: 'ACHC Accreditation Preparation',
    tagline: 'From zero to accredited — the right way.',
    desc: 'Our flagship service provides end-to-end guidance through every phase of the ACHC accreditation process. We conduct a thorough initial readiness assessment, identify compliance gaps, build your documentation framework, and walk alongside your team from application to final approval.',
    features: [
      'Initial readiness assessment & gap analysis',
      'ACHC application guidance and submission support',
      'Documentation framework development',
      'Standards interpretation and implementation',
      'Pre-survey preparation and final review',
      'Post-accreditation compliance planning',
    ],
    ideal: 'New home health agencies and those seeking initial ACHC accreditation.',
  },
  {
    id: 2,
    icon: 'ri-survey-line',
    title: 'Mock Surveys & Readiness Assessments',
    tagline: 'Know exactly where you stand before the real survey.',
    desc: 'Our mock survey service replicates the actual ACHC survey experience with the same rigor and standards. We evaluate your agency across all applicable ACHC standards, identify deficiencies, and provide a detailed corrective action plan to ensure you pass with confidence.',
    features: [
      'Full simulation of ACHC on-site survey process',
      'Evaluation against all applicable ACHC standards',
      'Detailed findings report with deficiency scoring',
      'Prioritized corrective action plan',
      'Staff interview preparation coaching',
      'Follow-up readiness verification',
    ],
    ideal: 'Agencies preparing for an upcoming ACHC survey or renewal.',
  },
  {
    id: 3,
    icon: 'ri-file-text-line',
    title: 'Policy & Procedure Development',
    tagline: 'ACHC-compliant documentation built for your agency.',
    desc: 'We develop a complete, customized policy and procedure manual tailored to your agency\'s operations and fully aligned with ACHC standards. Every document is written to reflect real-world workflows while meeting all regulatory requirements.',
    features: [
      'Custom policy and procedure manual creation',
      'ACHC standards alignment and cross-referencing',
      'Clinical, administrative, and operational policies',
      'Infection control and safety protocols',
      'HR and employment policy development',
      'Annual review and update support',
    ],
    ideal: 'Agencies lacking compliant documentation or needing a complete policy overhaul.',
  },
  {
    id: 4,
    icon: 'ri-search-eye-line',
    title: 'Compliance Audits',
    tagline: 'Identify gaps before they become deficiencies.',
    desc: 'Our compliance audit service provides a comprehensive review of your agency\'s current compliance posture. We assess documentation, operational practices, clinical records, and administrative processes against ACHC standards to uncover vulnerabilities and implement corrective measures.',
    features: [
      'Comprehensive compliance posture review',
      'Clinical record and documentation audit',
      'Operational and administrative process review',
      'ACHC standards gap analysis',
      'Risk prioritization and scoring',
      'Corrective action plan with timelines',
    ],
    ideal: 'Agencies experiencing compliance challenges or preparing for re-accreditation.',
  },
  {
    id: 5,
    icon: 'ri-group-line',
    title: 'Staff Training',
    tagline: 'Equip your team to sustain compliance every day.',
    desc: 'We design and deliver targeted training programs that build your staff\'s knowledge of ACHC standards, compliance responsibilities, and best practices. Training is customized to your agency\'s specific needs and can be delivered on-site or virtually.',
    features: [
      'ACHC standards awareness training',
      'Role-specific compliance education',
      'Survey readiness and staff interview coaching',
      'Documentation and record-keeping training',
      'Infection control and safety training',
      'Ongoing competency assessment support',
    ],
    ideal: 'Agencies onboarding new staff or strengthening their compliance culture.',
  },
  {
    id: 6,
    icon: 'ri-customer-service-2-line',
    title: 'Ongoing Compliance Support',
    tagline: 'Stay compliant long after accreditation is achieved.',
    desc: 'Our ongoing support retainer provides continuous advisory services to help your agency sustain compliance, adapt to regulatory changes, and operate at peak performance. Think of us as your dedicated compliance partner — always available when you need guidance.',
    features: [
      'Monthly compliance check-ins and reviews',
      'Regulatory change monitoring and updates',
      'Policy and procedure maintenance',
      'Staff training refreshers and updates',
      'Incident response and corrective action support',
      'Priority access to consultant for urgent matters',
    ],
    ideal: 'Accredited agencies seeking to maintain compliance and avoid deficiencies.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services#webpage`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
  name: 'ACHC Accreditation Services | Advantixx Home Health Consultants',
  description: 'Comprehensive ACHC accreditation and compliance solutions for home health agencies — including mock surveys, policy development, compliance audits, staff training, and ongoing support.',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website` },
  mainEntity: {
    '@type': 'ItemList',
    name: 'ACHC Accreditation Consulting Services',
    itemListElement: services.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: s.title,
        description: s.desc,
        provider: {
          '@type': 'Organization',
          name: 'Advantixx Home Health Accreditation Consultants',
        },
      },
    })),
  },
};

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(services[0]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', agency: '', service: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useSEO({
    title: 'ACHC Accreditation Services | Advantixx Home Health Consultants',
    description: 'Comprehensive ACHC accreditation and compliance solutions for home health agencies — including mock surveys, policy development, compliance audits, staff training, and ongoing support.',
    keywords: 'ACHC accreditation services, mock survey, policy procedure development, compliance audit, staff training, home health consulting',
    canonical: '/services',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-services');
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const body = new URLSearchParams(formData as Record<string, string>);
      const res = await fetch('https://readdy.ai/api/form/d7cm221noju8nk5a0ugg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', agency: '', service: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d5659d9154ae430b410077a924c39ffc.jpg"
            alt="Accreditation Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/85"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#d4af37]"></div>
            <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">What We Offer</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6">Our Services</h1>
          <p className="text-[#b0b0b0] text-lg max-w-2xl leading-relaxed">
            Comprehensive ACHC accreditation and compliance solutions tailored to your home health agency&apos;s unique needs.
          </p>
        </div>
      </section>

      {/* Services Explorer */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <p className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold mb-5">Select a Service</p>
              <div className="flex flex-col gap-2">
                {services.map((svc) => (
                  <button
                    key={svc.id}
                    onClick={() => setActiveService(svc)}
                    className={`flex items-center gap-4 px-5 py-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                      activeService.id === svc.id
                        ? 'bg-[#d4af37]/15 border border-[#d4af37]/50'
                        : 'bg-[#161616] border border-transparent hover:border-[#d4af37]/20'
                    }`}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${
                      activeService.id === svc.id ? 'bg-[#d4af37]/20' : 'bg-[#d4af37]/8'
                    }`}>
                      <i className={`${svc.icon} text-[#d4af37] text-base`}></i>
                    </div>
                    <span className={`text-sm font-medium leading-snug ${
                      activeService.id === svc.id ? 'text-white' : 'text-[#999]'
                    }`}>
                      {svc.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-2 bg-[#161616] border border-[#d4af37]/15 rounded-2xl p-8 md:p-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4af37]/15 mb-6">
                <i className={`${activeService.icon} text-[#d4af37] text-2xl`}></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2">{activeService.title}</h2>
              <p className="text-[#d4af37] text-sm font-medium mb-5 italic">{activeService.tagline}</p>
              <p className="text-[#999] text-base leading-relaxed mb-8">{activeService.desc}</p>

              <div className="mb-8">
                <p className="text-white font-bold text-sm uppercase tracking-wider mb-4">What&apos;s Included</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeService.features.map((f) => (
                    <div key={f} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#d4af37] text-sm"></i>
                      </div>
                      <span className="text-[#bbb] text-sm leading-relaxed">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#d4af37]/8 border border-[#d4af37]/20 rounded-xl p-5 mb-8">
                <p className="text-[#d4af37] text-xs uppercase tracking-wider font-semibold mb-1">Ideal For</p>
                <p className="text-[#ccc] text-sm leading-relaxed">{activeService.ideal}</p>
              </div>

              <button
                onClick={() => { setShowModal(true); setFormData(prev => ({ ...prev, service: activeService.title })); }}
                className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer"
              >
                Book a Consultation for This Service
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">How It Works</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white">Our Proven Process</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', icon: 'ri-phone-line', title: 'Initial Consultation', desc: 'We assess your agency\'s current state, goals, and accreditation timeline.' },
              { step: '02', icon: 'ri-file-search-line', title: 'Gap Analysis', desc: 'A thorough review of your documentation, processes, and compliance posture.' },
              { step: '03', icon: 'ri-tools-line', title: 'Implementation', desc: 'We build your compliance system, policies, and prepare your team for success.' },
              { step: '04', icon: 'ri-trophy-line', title: 'Accreditation', desc: 'Your agency passes the ACHC survey with confidence and achieves accreditation.' },
            ].map((step) => (
              <div key={step.step} className="relative bg-[#161616] border border-[#d4af37]/10 rounded-xl p-7 hover:border-[#d4af37]/30 transition-all duration-300">
                <span className="absolute top-5 right-5 text-[#d4af37]/20 font-black text-4xl leading-none">{step.step}</span>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 mb-5">
                  <i className={`${step.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-[#888] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Not Sure Which Service <span className="text-[#d4af37]">You Need?</span>
          </h2>
          <p className="text-[#888] text-base mb-8 leading-relaxed">
            Book a free discovery call and we&apos;ll help you identify exactly what your agency needs to achieve and maintain ACHC accreditation.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-10 py-4 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer"
          >
            Book a Free Discovery Call
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      <Footer />

      {/* Consultation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#161616] border border-[#d4af37]/20 rounded-2xl w-full max-w-lg p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => { setShowModal(false); setStatus('idle'); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#888] hover:text-[#d4af37] cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <h3 className="text-white font-black text-2xl mb-2">Book a Consultation</h3>
            <p className="text-[#888] text-sm mb-6">Fill out the form and our team will reach out within 24 hours.</p>

            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#d4af37]/20 mx-auto mb-4">
                  <i className="ri-check-line text-[#d4af37] text-3xl"></i>
                </div>
                <p className="text-white font-bold text-lg mb-2">Request Received!</p>
                <p className="text-[#888] text-sm">We&apos;ll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jane Smith"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jane@agency.com"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Agency Name</label>
                  <input
                    type="text"
                    name="agency"
                    value={formData.agency}
                    onChange={handleChange}
                    placeholder="Your Home Health Agency"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Service of Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-[#d4af37]/60 transition-colors cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your accreditation needs..."
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors resize-none"
                  />
                  <p className="text-[#555] text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
                {status === 'error' && (
                  <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer disabled:opacity-60"
                >
                  {status === 'loading' ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
